import { createSlice } from "@reduxjs/toolkit";
import * as actions from './api';

export const contentSlice = createSlice({
    name: 'content',
    initialState: {
        translatingIndexes: [],
        translationLoading: false,
        translationError: null,
        translationResults: {}, // Store translation results by sentenceIndex
    },
    reducers: {
        translationRequested: (state, action) => {
            state.translationLoading = true;
            const { sentenceIndex } = action.payload;
            
            // Ensure translatingIndexes is always an array (handle legacy Set format from persistence)
            if (!Array.isArray(state.translatingIndexes)) {
                state.translatingIndexes = Array.from(state.translatingIndexes || []);
            }
            
            if (!state.translatingIndexes.includes(sentenceIndex)) {
                state.translatingIndexes.push(sentenceIndex);
            }
        },
        translationReceived: (state, action) => {
            state.translationLoading = false;
            const { sentenceIndex, translations } = action.payload;
            
            // Ensure translatingIndexes is always an array (handle legacy Set format from persistence)
            if (!Array.isArray(state.translatingIndexes)) {
                state.translatingIndexes = Array.from(state.translatingIndexes || []);
            }
            
            state.translatingIndexes = state.translatingIndexes.filter(index => index !== sentenceIndex);
            // Store translation result
            state.translationResults[sentenceIndex] = translations;
        },
        translationError: (state, action) => {
            state.translationLoading = false;
            const { sentenceIndex } = action.payload;
            
            // Ensure translatingIndexes is always an array (handle legacy Set format from persistence)
            if (!Array.isArray(state.translatingIndexes)) {
                state.translatingIndexes = Array.from(state.translatingIndexes || []);
            }
            
            state.translatingIndexes = state.translatingIndexes.filter(index => index !== sentenceIndex);
            state.translationError = action.payload.error;
        },
        clearTranslationError: (state) => {
            state.translationError = null;
        },
        clearTranslationResult: (state, action) => {
            const { sentenceIndex } = action.payload;
            delete state.translationResults[sentenceIndex];
        }
    }
});

const {
    translationRequested,
    translationReceived,
    translationError,
    clearTranslationError,
    clearTranslationResult,
} = contentSlice.actions;

// Content management functions that work with formik
export const contentActions = {
    addNewSentence: (formik) => {
        const newSentence = {
            content: '',
            translation: []
        };
        const updatedContentExt = [...formik.values.content_ext, newSentence];
        formik.setFieldValue('content_ext', updatedContentExt);
    },

    deleteSentence: (formik, sentenceIndex) => {
        const updatedContentExt = formik.values.content_ext.filter((_, index) => index !== sentenceIndex);
        formik.setFieldValue('content_ext', updatedContentExt);
    },

    moveSentence: (formik, sentenceIndex, direction) => {
        const newIndex = direction === 'up' ? sentenceIndex - 1 : sentenceIndex + 1;
        if (newIndex < 0 || newIndex >= formik.values.content_ext.length) return;

        const updatedContentExt = [...formik.values.content_ext];
        const [movedSentence] = updatedContentExt.splice(sentenceIndex, 1);
        updatedContentExt.splice(newIndex, 0, movedSentence);
        formik.setFieldValue('content_ext', updatedContentExt);
    },

    updateSentenceContent: (formik, sentenceIndex, content) => {
        const updatedContentExt = [...formik.values.content_ext];
        updatedContentExt[sentenceIndex] = {
            ...updatedContentExt[sentenceIndex],
            content: content
        };
        formik.setFieldValue('content_ext', updatedContentExt);
    },

    addTranslation: (formik, sentenceIndex) => {
        const updatedContentExt = [...formik.values.content_ext];
        const newTranslation = {
            language_code: '',
            text: ''
        };
        
        if (!updatedContentExt[sentenceIndex].translation) {
            updatedContentExt[sentenceIndex].translation = [];
        }
        
        updatedContentExt[sentenceIndex].translation.push(newTranslation);
        formik.setFieldValue('content_ext', updatedContentExt);
    },

    deleteTranslation: (formik, sentenceIndex, translationIndex) => {
        const updatedContentExt = [...formik.values.content_ext];
        updatedContentExt[sentenceIndex].translation = updatedContentExt[sentenceIndex].translation.filter(
            (_, index) => index !== translationIndex
        );
        formik.setFieldValue('content_ext', updatedContentExt);
    },

    updateTranslation: (formik, sentenceIndex, translationIndex, field, value) => {
        const updatedContentExt = [...formik.values.content_ext];
        updatedContentExt[sentenceIndex].translation[translationIndex] = {
            ...updatedContentExt[sentenceIndex].translation[translationIndex],
            [field]: value
        };
        formik.setFieldValue('content_ext', updatedContentExt);
    }
};

// Async action for translation API call
const translateSentence = (formik, sentenceIndex) => (dispatch, getState) => {
    const accessToken = getState().auth.accessToken;
    const sentence = formik.values.content_ext[sentenceIndex];
    
    if (!sentence.content || sentence.content.trim() === '') {
        return;
    }

    dispatch(translationRequested({ sentenceIndex }));

    dispatch(
        actions.apiCallBegan({
            url: '/translate',
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            data: {
                content: sentence.content
            },
            onSuccess: translationReceived.type,
            onError: translationError.type,
            metadata: { sentenceIndex }
        })
    );
};

export {
    translateSentence,
    clearTranslationError,
    clearTranslationResult,
};

export default contentSlice.reducer; 